import React, { useMemo, useRef, useEffect } from "react";
import { Loader2 } from "lucide-react";
import Text from "@src/lib/ui/Text";
import PenWritingOutlineIcon from "@src/lib/icons/18px/pen-writing-outline";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import { Button } from "@src/lib/ui/Button";
import { useChatContext } from "@src/features/assistantChat/ChatRuntimeProvider";
import { AiChatType } from "@src/types.generated";
import { cn } from "@src/lib/utils/cn";

interface Props {
  isStreaming: boolean;
  onClick?: () => void;
  canvasContent: React.ReactNode;
  isViewing?: boolean;
}

const CanvasWidget: React.FC<Props> = ({
  isStreaming,
  onClick,
  canvasContent,
  isViewing,
}) => {
  const { chatType } = useChatContext();
  const canvasTitle = useMemo(() => {
    switch (chatType) {
      case AiChatType.ModuleOutline:
        return "Module outline";
      case AiChatType.CourseOutline:
        return "Course outline";
      case AiChatType.TaskListOutline:
        return `Checklist outline`;
      case AiChatType.CheckInOutline:
        return "Check-in outline";
      case AiChatType.AuditOutline:
        return "Audit outline";
      default:
        return "Outline";
    }
  }, [chatType]);

  return (
    <div className="flex flex-col overflow-hidden rounded-[16px] border border-input bg-white">
      <CanvasHeader
        title={canvasTitle}
        loading={isStreaming}
        isViewing={isViewing}
        onClick={onClick}
      />
      <CanvasContent canvasContent={canvasContent} loading={isStreaming} />
    </div>
  );
};

interface CanvasHeaderProps {
  title: string;
  loading: boolean;
  isViewing?: boolean;
  onClick?: () => void;
}

const CanvasHeader = React.memo<CanvasHeaderProps>(
  ({ title, loading, isViewing, onClick }) => {
    return (
      <div
        className={cn(
          "bg-gray-alpha-3 flex items-center justify-between px-3 py-1 border-b border-input h-[40px]",
          !loading && !isViewing && "pr-2",
        )}
      >
        <div className="flex items-center gap-2">
          {loading ? (
            <>
              <PenWritingOutlineIcon className="size-[16px] text-muted-foreground" />
              <Text variant="fine-print-title">Writing outline...</Text>
            </>
          ) : (
            <>
              <PenOutlineIcon className="size-[16px] text-muted-foreground" />
              <Text variant="fine-print-title">{title}</Text>
            </>
          )}
        </div>
        <div>
          {loading && <Loader2 className="size-3 animate-spin" />}
          {!loading && isViewing && (
            <div className="flex-1 flex items-center gap-1 justify-end">
              <CheckmarkIcon className="text-muted-foreground size-[12px]" />
              <Text
                variant="fine-print-title"
                className="text-muted-foreground"
              >
                Viewing
              </Text>
            </div>
          )}
          {!loading && !isViewing && (
            <div className="flex-1 flex items-center justify-end">
              <Button
                variant="outline"
                size="xs"
                className="text-primary"
                onClick={() => onClick?.()}
              >
                View
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

CanvasHeader.displayName = "CanvasHeader";

interface CanvasContentProps {
  canvasContent: React.ReactNode;
  loading: boolean;
}

const CanvasContent = React.memo<CanvasContentProps>(
  ({ canvasContent, loading }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, [canvasContent]);

    return (
      <div className="relative h-[100px] overflow-hidden">
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto p-2 pb-1 canvas-widget"
        >
          {canvasContent}
        </div>
        <div
          className={cn(
            "pointer-events-none absolute left-0 right-0 bottom-0 h-[30px] bg-gradient-to-t from-white to-transparent transition-all",
            loading ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
    );
  },
);

CanvasContent.displayName = "CanvasContent";

export default CanvasWidget;
